<script setup lang="ts">
  import { useEventListener, useMediaQuery, useResizeObserver } from '@vueuse/core'
  import { computed, ref, watch, type CSSProperties } from 'vue'

  defineOptions({ inheritAttrs: false })

  const props = defineProps<{
    open?: boolean
    bottom?: boolean
    right?: boolean
    left?: boolean
    scroll?: boolean
    forceFlip?: boolean
    trigger?: HTMLElement | null
    matchTrigger?: boolean
  }>()

  const menuEl = ref<HTMLElement | null>(null)
  const anchor = ref<HTMLElement | null>(null)
  const isMobile = useMediaQuery('(max-width: 640px)')

  const triggerRect = ref<DOMRect | null>(null)
  const vw = ref(0)
  const vh = ref(0)

  function refEl(): HTMLElement | null {
    return props.trigger ?? anchor.value?.parentElement ?? null
  }

  const stackGap = 4
  const sideGap = 12

  const flip = computed(() => {
    const rect = triggerRect.value

    if (!rect || isMobile.value) return false
    if (props.forceFlip) return true

    return rect.top > 250 && rect.bottom + 250 > vh.value
  })

  const positionStyle = computed<CSSProperties>(() => {
    const rect = triggerRect.value
    if (!rect) return {}

    const widthStyle: CSSProperties = props.matchTrigger
      ? { width: `${rect.width}px`, minWidth: '0', maxWidth: `${rect.width}px` }
      : {}

    if (isMobile.value) {
      return { top: `${rect.bottom + stackGap}px`, left: '0.75rem', right: '0.75rem', ...widthStyle }
    }

    let verticalStyle: CSSProperties
    let horizontalStyle: CSSProperties

    if (props.bottom) {
      // Menu stacks below the trigger (or above when flipped).
      verticalStyle = flip.value
        ? { bottom: `${vh.value - rect.top + stackGap}px` }
        : { top: `${rect.bottom + stackGap}px` }

      // Horizontally aligns with the trigger's left or right edge.
      horizontalStyle = props.right ? { left: `${rect.left}px` } : { right: `${vw.value - rect.right}px` }
    } else {
      // Menu sits beside the trigger, vertically aligned to its top (or bottom when flipped).
      verticalStyle = flip.value ? { bottom: `${vh.value - rect.bottom}px` } : { top: `${rect.top}px` }

      // Placed to the right or left of the trigger with a horizontal gap.
      horizontalStyle = props.right
        ? { left: `${rect.right + sideGap}px` }
        : { right: `${vw.value - rect.left + sideGap}px` }
    }

    return { ...verticalStyle, ...horizontalStyle, ...widthStyle }
  })

  function update() {
    const el = refEl()
    if (!el) return

    triggerRect.value = el.getBoundingClientRect()
    vw.value = document.documentElement.clientWidth
    vh.value = document.documentElement.clientHeight
  }

  const activeTarget = computed(() => (props.open ? refEl() : null))
  const activeWindow = computed(() => (props.open ? window : null))

  useEventListener(activeWindow, 'scroll', update, { capture: true, passive: true })
  useEventListener(activeWindow, 'resize', update)
  useResizeObserver(activeTarget, update)

  watch(
    () => props.open,
    (v) => v && update(),
    { immediate: true },
  )

  defineExpose({ menuEl })
</script>

<template>
  <span ref="anchor" class="hidden"></span>
  <Teleport to="body">
    <Transition enter-from-class="scale-y-0 pointer-events-none" leave-to-class="scale-y-0 pointer-events-none">
      <div
        v-show="open"
        v-bind="$attrs"
        ref="menuEl"
        :style="positionStyle"
        class="fixed flex flex-col bg-white w-40 min-w-fit border border-white-gray p-2 gap-2 z-50 shadow-lg transition-transform rounded-lg"
        :class="{
          'origin-bottom': flip,
          'origin-top': !flip,
          'overflow-y-scroll max-h-72 xl:max-h-96': scroll,
        }"
      >
        <slot></slot>
      </div>
    </Transition>
  </Teleport>
</template>
